import type { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import { selectors, withOffset } from '../../Stream';
import { getCurrentUser } from '../../publicSession/selectors';
import { requestPuppy } from '@wix/da-http-client';
import { GallectionDeviationStreamParams } from '../types';
import { GallectionType } from '../../types/gallection';
import { getGrusername } from '../selectors';
import type {
  PapiRequestDaSharedGallectionContents,
  PapiRequestDaSharedGallectionSearch,
  PapiDaSharedGallectionContents,
  PapiDaSharedGallectionSearch,
} from '@wix/da-papi-types';
import { SCRAPS_FOLDER_ID, ALL_FOLDER_ID } from '@wix/da-url';
import type { FetchAction } from '../../Stream/base/types';
import { getGallectionType } from '../helpers';
import { normalizeDeviations } from '../../redux/normalizr/helpers';
import { FetchType } from '../../Stream/base/types';

export function fakeResponse({ params }): any {
  if (process.env.NODE_ENV !== 'production') {
    const { deviationsByType } = require('@wix/da-sample-data'); // eslint-disable-line global-require
    const { Chance } = require('chance'); // eslint-disable-line global-require
    if (window.doError) {
      return {
        error: 'invalid_request',
        error_description: 'Not logged in.',
        status: 'error',
      };
    }

    const { folderId, offset, limit } = params;

    if (folderId === -999) {
      return {
        hasMore: false,
        nextOffset: 0,
        results: [],
      };
    }

    const randomDeviations = new Chance().pickset(
      deviationsByType.pixel
        .concat(deviationsByType.statuses)
        .concat(deviationsByType.image)
        .concat(deviationsByType.edgeCases)
        .concat(deviationsByType.journal)
        .concat(deviationsByType.literature),
      limit
    );

    return {
      hasMore: offset + limit < deviationsByType.image.length,
      nextOffset: offset + limit,
      results: randomDeviations.slice(0, limit),
    };
  }
}

function* fetch(url: string, params: object) {
  const response = yield call(
    requestPuppy,
    {
      method: 'get',
      url,
      params,
    },
    fakeResponse
  );
  return response;
}

function* handleDeviationStreamFetch(action: FetchAction): SagaIterator {
  const { streamId } = action;

  const streamParams: Partial<GallectionDeviationStreamParams> = yield select(
    selectors.getStreamParamsSafe,
    streamId
  );

  const { folderId, mediaSource, searchQuery } = streamParams;

  const gallectionType = getGallectionType(mediaSource);

  const {
    currentUser,
    offset,
    limit,
    grusername,
  }: {
    currentUser: ReturnType<typeof getCurrentUser>;
    offset: ReturnType<typeof withOffset.selectors.getNextOffset>;
    limit: ReturnType<typeof selectors.getItemsPerFetch>;
    grusername: string | undefined;
  } = yield select(state => ({
    currentUser: getCurrentUser(state),
    offset: withOffset.selectors.getNextOffset(state, streamId),
    limit: selectors.getItemsPerFetch(state, streamId),
    grusername: getGrusername(state),
  }));

  let url: string = '';
  let params: object = {};

  // Groups do not have sta.sh, so we always have to interface with the user's sta.sh
  const username =
    (gallectionType === GallectionType.STASH
      ? currentUser.username
      : grusername) || currentUser.username;

  if (searchQuery) {
    if (gallectionType !== GallectionType.STASH) {
      url = 'gallection/search';
      const gallectionSearchParams: PapiRequestDaSharedGallectionSearch = {
        username,
        type: gallectionType ?? 'gallery',
        q: searchQuery,
        offset,
        limit,
      };
      params = gallectionSearchParams;
    }
  } else {
    url = 'gallection/contents';
    const gallectionContentsEndpointParams: PapiRequestDaSharedGallectionContents =
      {
        username,
        type: gallectionType ?? 'collection',
        folderid: folderId,
        offset,
        limit,
        mature_content: true,
      };
    if (folderId === SCRAPS_FOLDER_ID) {
      gallectionContentsEndpointParams.scraps_folder = true;
    } else if (folderId === ALL_FOLDER_ID) {
      gallectionContentsEndpointParams.all_folder = true;
    }
    params = gallectionContentsEndpointParams;
  }

  if (!url) {
    return;
  }

  const response:
    | PapiDaSharedGallectionSearch
    | PapiDaSharedGallectionContents
    | false = yield call(fetch, url, params);

  // TODO: proper error handling
  if (!response) {
    yield put(
      withOffset.actions.fetchFailure({
        streamId,
        fetchType: FetchType.Next,
        errorMsg: 'Failed to fetch',
      })
    );
    return;
  }

  const { hasMore, results, nextOffset } = response;
  const normalized = results && normalizeDeviations(results);

  yield put(
    withOffset.actions.fetchSuccessDeduped({
      streamId,
      hasMore: results ? !!hasMore : false,
      items: normalized?.result ?? [],
      entities: normalized?.entities ?? {},
      nextOffset: nextOffset ?? undefined,
    })
  );
}

export default handleDeviationStreamFetch;
